import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  DollarSign, Building2, Loader2, CheckCircle, Clock, 
  XCircle, Send, Edit2, Plus, AlertTriangle, RefreshCw,
  ArrowRight, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const FundManagement = ({ campaignId, campaign }) => {
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fundSummary, setFundSummary] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showModeChangeModal, setShowModeChangeModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Bank account form
  const [bankForm, setBankForm] = useState({
    account_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    bank_name: '',
    notification_email: ''
  });
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    amount: '',
    notes: ''
  });
  
  // Mode change form
  const [modeChangeForm, setModeChangeForm] = useState({
    new_mode: '',
    reason: ''
  });

  useEffect(() => {
    if (campaign?.fund_collection_mode === 'kaizen_managed') {
      fetchFundData();
    } else {
      setLoading(false);
    }
  }, [campaignId, campaign]);

  const fetchFundData = async () => {
    try {
      setLoading(true);
      const [summaryRes, transfersRes] = await Promise.all([
        api.get(`/fund-transfers/campaign/${campaignId}/summary`),
        api.get(`/fund-transfers/campaign/${campaignId}/transfers`)
      ]);
      
      setFundSummary(summaryRes.data);
      setTransfers(transfersRes.data);
      
      // Pre-fill bank form if exists
      if (summaryRes.data.bank_account) {
        setBankForm({
          account_name: summaryRes.data.bank_account.account_name || '',
          account_number: '',
          confirm_account_number: '',
          ifsc_code: summaryRes.data.bank_account.ifsc_code || '',
          bank_name: summaryRes.data.bank_account.bank_name || '',
          notification_email: summaryRes.data.bank_account.notification_email || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch fund data:', error);
      toast.error('Failed to load fund data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    
    if (bankForm.account_number !== bankForm.confirm_account_number) {
      toast.error('Account numbers do not match');
      return;
    }
    
    setSubmitting(true);
    try {
      const endpoint = fundSummary?.has_bank_account 
        ? `/fund-transfers/campaign/${campaignId}/bank-account`
        : `/fund-transfers/campaign/${campaignId}/bank-account`;
      
      const method = fundSummary?.has_bank_account ? 'put' : 'post';
      
      await api[method](endpoint, {
        campaign_id: campaignId,
        account_name: bankForm.account_name,
        account_number: bankForm.account_number,
        ifsc_code: bankForm.ifsc_code,
        bank_name: bankForm.bank_name,
        notification_email: bankForm.notification_email
      });
      
      toast.success('Bank account request submitted for approval');
      setShowBankModal(false);
      fetchFundData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    try {
      await api.post('/fund-transfers', {
        campaign_id: campaignId,
        amount: parseFloat(transferForm.amount),
        notes: transferForm.notes
      });
      
      toast.success('Transfer request submitted for approval');
      setShowTransferModal(false);
      setTransferForm({ amount: '', notes: '' });
      fetchFundData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleModeChange = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    try {
      await api.post('/fund-transfers/change-requests', {
        campaign_id: campaignId,
        request_type: 'fund_collection_mode',
        current_value: { fund_collection_mode: campaign.fund_collection_mode },
        new_value: { fund_collection_mode: modeChangeForm.new_mode },
        reason: modeChangeForm.reason
      });
      
      toast.success('Mode change request submitted for approval');
      setShowModeChangeModal(false);
      setModeChangeForm({ new_mode: '', reason: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit change request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Pending Approval' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Rejected' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Completed' }
    };
    const badge = badges[status] || badges.pending_approval;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  // If fund collection mode is not kaizen_managed, show minimal UI
  if (campaign?.fund_collection_mode !== 'kaizen_managed') {
    return (
      <div className="space-y-6">
        <Card className="bg-white border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Direct to Organizer</h3>
              <p className="text-sm text-slate-500">Payments go directly to organizer's payment gateway</p>
            </div>
          </div>
          
          {isSuperAdmin() && (
            <Button 
              variant="outline" 
              onClick={() => {
                setModeChangeForm({ new_mode: 'kaizen_managed', reason: '' });
                setShowModeChangeModal(true);
              }}
              className="mt-4"
            >
              Switch to Kaizen Managed
            </Button>
          )}
        </Card>

        {/* Mode Change Modal */}
        <Dialog open={showModeChangeModal} onOpenChange={setShowModeChangeModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Change Fund Collection Mode</DialogTitle>
              <DialogDescription>
                Switch to Kaizen Managed fund collection
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleModeChange} className="space-y-4">
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <p className="text-sm text-violet-700">
                  <strong>Kaizen Managed:</strong> Kaizen will collect payments on behalf of the organizer 
                  and transfer funds to their bank account (2.95% platform fee applies).
                </p>
              </div>
              
              <div>
                <Label>Reason for Change *</Label>
                <Textarea
                  value={modeChangeForm.reason}
                  onChange={(e) => setModeChangeForm({ ...modeChangeForm, reason: e.target.value })}
                  placeholder="Why are you changing the fund collection mode?"
                  rows={3}
                  required
                  className="mt-1"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-700">
                  This change requires Director approval.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setShowModeChangeModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || !modeChangeForm.reason} className="flex-1">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit for Approval
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Fund Summary */}
      {fundSummary?.view_type === 'super_admin' ? (
        // Super Admin sees full breakdown with gateway fees
        <div className="space-y-4">
          {/* Row 1: Collection & Gateway */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Total Collected</p>
              <p className="text-2xl font-bold text-slate-900">
                ₹{(fundSummary?.total_collected || 0).toLocaleString()}
              </p>
              <p className="text-[10px] text-slate-400">Amount users paid</p>
            </Card>
            <Card className="bg-white border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Gateway Fees</p>
              <p className="text-2xl font-bold text-red-500">
                ₹{(fundSummary?.gateway_fees || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-400">Payment gateway charges</p>
            </Card>
            <Card className="bg-green-50 border-green-200 p-4">
              <p className="text-xs text-green-700 mb-1">Gateway Settled</p>
              <p className="text-2xl font-bold text-green-700">
                ₹{(fundSummary?.gateway_settled || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-green-600">In Kaizen's account</p>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200 p-4">
              <p className="text-xs text-yellow-700 mb-1">Gateway Pending</p>
              <p className="text-2xl font-bold text-yellow-700">
                ₹{(fundSummary?.gateway_pending || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-yellow-600">With payment gateway</p>
            </Card>
          </div>
          
          {/* Row 2: Platform Fee & Transfers */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-violet-50 border-violet-200 p-4">
              <p className="text-xs text-violet-700 mb-1">Platform Fee (2.95%)</p>
              <p className="text-2xl font-bold text-violet-700">
                ₹{(fundSummary?.platform_fee || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              {/* Kaizen's Share — only visible to Directors (matches /fund-transfers page) */}
              {user?.can_approve_transfers && (
                <p className="text-[10px] text-violet-600">Kaizen's share: ₹{(fundSummary?.kaizen_share || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
              )}
            </Card>
            <Card className="bg-white border-slate-200 p-4">
              <p className="text-xs text-slate-500 mb-1">Net Payable</p>
              <p className="text-2xl font-bold text-slate-900">
                ₹{(fundSummary?.net_payable || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-400">To organizer</p>
            </Card>
            <Card className="bg-green-50 border-green-200 p-4">
              <p className="text-xs text-green-700 mb-1">Transferred</p>
              <p className="text-2xl font-bold text-green-700">
                ₹{(fundSummary?.transferred || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-green-600">Paid to organizer</p>
            </Card>
            <Card className="bg-orange-50 border-orange-200 p-4">
              <p className="text-xs text-orange-700 mb-1">Pending</p>
              <p className="text-2xl font-bold text-orange-700">
                ₹{(fundSummary?.pending || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-orange-600">Yet to transfer</p>
            </Card>
          </div>
        </div>
      ) : (
        // Org Admin sees simplified view
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200 p-4">
            <p className="text-xs text-slate-500 mb-1">Total Collections</p>
            <p className="text-2xl font-bold text-slate-900">
              ₹{(fundSummary?.total_collected || 0).toLocaleString()}
            </p>
            <p className="text-[10px] text-slate-400">Gross amount collected</p>
          </Card>
          <Card className="bg-blue-50 border-blue-200 p-4">
            <p className="text-xs text-blue-700 mb-1">Amount Receivable</p>
            <p className="text-2xl font-bold text-blue-700">
              ₹{(fundSummary?.amount_receivable || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-blue-600">After platform charges</p>
          </Card>
          <Card className="bg-green-50 border-green-200 p-4">
            <p className="text-xs text-green-700 mb-1">Amount Received</p>
            <p className="text-2xl font-bold text-green-700">
              ₹{(fundSummary?.amount_received || 0).toLocaleString()}
            </p>
            <p className="text-[10px] text-green-600">Transferred to your account</p>
          </Card>
          <Card className="bg-orange-50 border-orange-200 p-4">
            <p className="text-xs text-orange-700 mb-1">Pending Transfer</p>
            <p className="text-2xl font-bold text-orange-700">
              ₹{(fundSummary?.amount_pending || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-orange-600">Yet to receive</p>
          </Card>
        </div>
      )}

      {/* Bank Account Section */}
      <Card className="bg-white border-slate-200">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Bank Account Details</h3>
              <p className="text-sm text-slate-500">
                {isSuperAdmin() ? 'Account for fund transfers' : 'Contact Super Admin to update'}
              </p>
            </div>
          </div>
          {isSuperAdmin() && (
            <Button variant="outline" size="sm" onClick={() => setShowBankModal(true)}>
              {fundSummary?.has_bank_account ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {fundSummary?.has_bank_account ? 'Edit' : 'Add'} Bank Account
            </Button>
          )}
        </div>
        
        {fundSummary?.bank_account ? (
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Account Name</p>
                <p className="font-medium text-slate-900">{fundSummary.bank_account.account_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Account Number</p>
                <p className="font-medium text-slate-900">{fundSummary.bank_account.account_number_masked}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">IFSC Code</p>
                <p className="font-medium text-slate-900">{fundSummary.bank_account.ifsc_code}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Bank Name</p>
                <p className="font-medium text-slate-900">{fundSummary.bank_account.bank_name}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-1">Notification Email</p>
              <p className="font-medium text-slate-900">{fundSummary.bank_account.notification_email}</p>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">No bank account configured</p>
            {!isSuperAdmin() && (
              <p className="text-sm text-slate-400">
                For transfer inquiries, contact: <a href="mailto:accounts@kaizeninfotech.com" className="text-primary hover:underline">accounts@kaizeninfotech.com</a>
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Transfer Actions (Super Admin Only) */}
      {isSuperAdmin() && fundSummary?.pending > 0 && fundSummary?.has_bank_account && (
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <div className="p-6 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Pending Transfer</h3>
              <p className="text-sm text-slate-600">
                ₹{fundSummary.pending.toLocaleString(undefined, { maximumFractionDigits: 0 })} available for transfer
              </p>
            </div>
            <Button 
              onClick={() => {
                setTransferForm({ amount: fundSummary.pending.toFixed(2), notes: '' });
                setShowTransferModal(true);
              }}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Create Transfer Request
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {/* Transfer History */}
      <Card className="bg-white border-slate-200">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Transfer History</h3>
          <Button variant="ghost" size="sm" onClick={fetchFundData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {transfers.length === 0 ? (
          <div className="p-8 text-center">
            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No transfers yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Reference</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transfers.map((transfer) => (
                  <tr key={transfer.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {format(parseISO(transfer.created_at), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                      ₹{transfer.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {transfer.transaction_reference || '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {getStatusBadge(transfer.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Mode Change Option (Super Admin Only) */}
      {isSuperAdmin() && (
        <Card className="bg-white border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Current Mode: <span className="font-medium">Kaizen Managed</span></p>
                <p className="text-xs text-slate-400">2.95% platform fee on all collections</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setModeChangeForm({ new_mode: 'organizer_direct', reason: '' });
                setShowModeChangeModal(true);
              }}
              disabled={fundSummary?.pending > 0}
            >
              Change to Direct
            </Button>
          </div>
          {fundSummary?.pending > 0 && (
            <p className="text-xs text-orange-500 mt-2">
              Complete all pending transfers before changing fund collection mode
            </p>
          )}
        </Card>
      )}

      {/* Bank Account Modal */}
      <Dialog open={showBankModal} onOpenChange={setShowBankModal}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {fundSummary?.has_bank_account ? 'Update' : 'Add'} Bank Account
            </DialogTitle>
            <DialogDescription>
              Enter bank account details for fund transfers
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveBankAccount} className="space-y-4">
            <div>
              <Label>Account Name *</Label>
              <Input
                value={bankForm.account_name}
                onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                placeholder="Name as per bank records"
                required
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                  placeholder="Account number"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Confirm Account Number *</Label>
                <Input
                  value={bankForm.confirm_account_number}
                  onChange={(e) => setBankForm({ ...bankForm, confirm_account_number: e.target.value })}
                  placeholder="Re-enter account number"
                  required
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>IFSC Code *</Label>
                <Input
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({ ...bankForm, ifsc_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., HDFC0001234"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Bank Name *</Label>
                <Input
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                  placeholder="e.g., HDFC Bank"
                  required
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Notification Email *</Label>
              <Input
                type="email"
                value={bankForm.notification_email}
                onChange={(e) => setBankForm({ ...bankForm, notification_email: e.target.value })}
                placeholder="Email for transfer notifications"
                required
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Transfer notifications will be sent to this email</p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">
                Bank account changes require Director approval before taking effect.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBankModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit for Approval
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Transfer Request</DialogTitle>
            <DialogDescription>
              Transfer funds to organizer's bank account
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTransfer} className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Available for Transfer:</span>
                <span className="font-semibold text-slate-900">
                  ₹{(fundSummary?.pending || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            
            <div>
              <Label>Transfer Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                <Input
                  type="number"
                  step="0.01"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                  className="pl-8"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={transferForm.notes}
                onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                placeholder="Settlement notes..."
                rows={3}
                className="mt-1"
              />
            </div>
            
            {fundSummary?.bank_account && (
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-xs font-medium text-blue-600 mb-2">Transfer To:</p>
                <p className="text-sm text-blue-800">
                  {fundSummary.bank_account.account_name} | {fundSummary.bank_account.account_number_masked}
                </p>
                <p className="text-xs text-blue-600">{fundSummary.bank_account.bank_name}</p>
              </div>
            )}
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">
                This request will be sent to a Director for approval.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTransferModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-violet-600 hover:bg-violet-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit for Approval
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mode Change Modal */}
      <Dialog open={showModeChangeModal} onOpenChange={setShowModeChangeModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Change Fund Collection Mode</DialogTitle>
            <DialogDescription>
              Switch to {modeChangeForm.new_mode === 'kaizen_managed' ? 'Kaizen Managed' : 'Direct to Organizer'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleModeChange} className="space-y-4">
            <div className={`border rounded-lg p-4 ${modeChangeForm.new_mode === 'kaizen_managed' ? 'bg-violet-50 border-violet-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm ${modeChangeForm.new_mode === 'kaizen_managed' ? 'text-violet-700' : 'text-green-700'}`}>
                {modeChangeForm.new_mode === 'kaizen_managed' 
                  ? 'Kaizen will collect payments and transfer to organizer (2.95% fee)'
                  : 'Payments will go directly to organizer\'s payment gateway'}
              </p>
            </div>
            
            <div>
              <Label>Reason for Change *</Label>
              <Textarea
                value={modeChangeForm.reason}
                onChange={(e) => setModeChangeForm({ ...modeChangeForm, reason: e.target.value })}
                placeholder="Why are you changing the fund collection mode?"
                rows={3}
                required
                className="mt-1"
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">
                This change requires Director approval.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModeChangeModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !modeChangeForm.reason} className="flex-1">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit for Approval
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundManagement;
